import { needsCallPreparation } from './lib/callScreen';
import { upgradeStatus } from './lib/messageStatus';
import { useTranslation } from 'react-i18next';
import { clearUserDrafts } from './lib/drafts';
import { prepareReleaseFeedback } from './lib/releaseFeedback';
import { isChatMutedAt } from './lib/chatMute';
import { cacheAltEndpoints, readCachedAltEndpoints } from './lib/altEndpoint';
import { canLoadOlderMessages, hideTechnicalDiagnostics, hideTechnicalSupportMessages, isTechnicalDiagnosticMessage, SUPPORT_DIAGNOSTIC_SCAN_MAX_PAGES } from './lib/technicalDiagnosticMessage';
import type { OlderHistoryResult } from './lib/emptyHistoryAutoload';
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { me, logout } from './api/auth';
import { isAuthenticated, api, randomUUID, ApiError, getAuthSession } from './api/client';
import { createChat, editMessagePayload, getChat, history, listChats, listMembers, markDelivered, markRead, MESSAGE_PAGE_SIZE } from './api/chats';
import { receiptThresholds } from './lib/receiptThresholds';
import { realtime } from './realtime';
import { outbox } from './lib/outbox';
import { RtcSession, rtcApp, type RtcSignal } from './rtc';
import { RtcV2Session, fetchCallSnapshot, isV2Event, replyUnavailable, rtcV2Enabled, type RtcV2Handlers } from './rtcV2';
import { rtcGroupV2Enabled } from './rtcProtocol';
import { AsyncAttemptGate, EndedCallIdRegistry, mayStartIncomingV2Call } from './rtcV2Lifecycle';
import type { V2Event } from './rtcV2Machine';
import { GroupCallSession, type GroupRtcSignal } from './rtcGroup';
import { GroupCallV2Session, type GroupCallV2Handlers } from './rtcGroupV2';
import { playMessageSound, startRingtone } from './sound';
import { useOutgoingRingback } from './lib/useOutgoingRingback';
import { notificationBody, requestNotificationPermission, showMessageNotification } from './notifications';
import { startAutoDiagnostics } from './lib/autoDiagnostics';
import { cachedSupportNotificationPrefs, getSupportNotificationPrefs, getSupportConversation, submitSupportRequest } from './api/support';
import { supportNotificationAllowed } from './lib/supportNotificationRules';
import { disablePushOnLogout, listenPushNavigation, markNotificationChatRead, registerServiceWorker, syncPushSubscription } from './push';
import { getAppSettings } from './api/settings';
import Login from './components/Login';
import AltEndpointBanner from './components/AltEndpointBanner';
import InstallAppBanner from './components/InstallAppBanner';
import AccessNeededBanner from './components/AccessNeededBanner';
import type { MediaFailure } from './lib/accessCheck';
import ChatList from './components/ChatList';
import SidebarResizer, { useSidebarWidth } from './components/SidebarResizer';
import ChatView from './components/ChatView';
import { remainingUnreadCount } from './lib/scrollToUnread';
import TransferPolicyPrompt from './components/TransferPolicyPrompt';
import RoomsColumn from './components/RoomsColumn';
import ErrorBoundary from './components/ErrorBoundary';
import JoinInviteModal from './components/JoinInviteModal';
import { ContactLinkPreviewModal } from './components/ContactQr';
import { clearPendingContactQr, readPendingContactQr } from './lib/contactQr';
import { getUserBrief } from './api/user';
import type { ContactRouteReference } from './lib/contactReference';
import {
  contactProfileError,
  contactReferenceHref,
  contactUsernameMismatch,
  desktopContactUri,
  parseStoredContactRoute,
  readContactRoute,
} from './lib/contactReference';
import type { ChatDto, ChatSummary, MemberDto, MessageDto, UserBriefDto, UserDto } from './api/types';

// Код звонков/настроек/админки не нужен подавляющему большинству рендеров — выносим в
// отдельные чанки, подгружаемые только когда реально открыты (звонок идёт / открыта
// модалка настроек / открыта админ-панель), а не сразу со всем остальным приложением.
import UpdateAvailableBanner from './components/UpdateAvailableBanner';
import type { UpdatePlatform } from './api/appUpdate';

const CallOverlay = lazy(() => import('./components/CallOverlay'));
const GroupCallOverlay = lazy(() => import('./components/GroupCallOverlay'));
const SettingsModal = lazy(() => import('./components/SettingsModal'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));

type CallMode = 'outgoing' | 'incoming' | 'preparing' | 'connecting' | 'active';

// Ключ истории сообщений в карте: `chatId` для общей комнаты, `chatId:roomId` для комнаты.
// Общая комната — roomId null (см. ChatRoomService).
const roomKey = (chatId: string, roomId?: string | null): string => (roomId ? chatId + ':' + roomId : chatId);

function unreadInRoom(chat: ChatSummary, roomId: string | null): number {
  if (chat.roomUnread) return chat.roomUnread.find((entry) => entry.roomId === roomId)?.count ?? 0;
  return roomId === null ? chat.unreadCount : 0;
}

function clearRoomUnread(chat: ChatSummary, roomId: string | null): ChatSummary {
  if (!chat.roomUnread) return roomId === null ? { ...chat, unreadCount: 0 } : chat;
  const roomUnread = chat.roomUnread.map((entry) => entry.roomId === roomId ? { ...entry, count: 0 } : entry);
  return { ...chat, roomUnread, unreadCount: roomUnread.reduce((sum, entry) => sum + entry.count, 0) };
}

function mobileRoomPickerActive(hasRooms: boolean): boolean {
  return hasRooms && window.matchMedia(
    '(max-width: 720px), (max-height: 520px) and (orientation: landscape) and (pointer: coarse)',
  ).matches;
}

function formatDuration(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const ss = String(s % 60).padStart(2, '0');
  if (h > 0) return `${h}:${String(m % 60).padStart(2, '0')}:${ss}`;
  return `${m}:${ss}`;
}

/** Токен из адреса `/join/<token>` (ссылка-приглашение) — запоминаем до входа, чтобы пережить экран логина. */
function readJoinToken(): string | null {
  const m = window.location.pathname.match(/^\/join\/([A-Za-z0-9_-]{1,32})$/);
  if (m) {
    try { sessionStorage.setItem('hc_joinToken', m[1]); } catch { /* приватный режим */ }
    window.history.replaceState(null, '', '/');
    return m[1];
  }
  try { return sessionStorage.getItem('hc_joinToken'); } catch { return null; }
}

/** Ссылка на контакт переживает экран входа, но адрес нормализуется после проверки UUID/username. */
function readPendingUserReference(): ContactRouteReference | null {
  const fromPath = readContactRoute(window.location.pathname, window.location.search);
  if (fromPath) {
    try { sessionStorage.setItem('hc_pendingUserReference', contactReferenceHref(fromPath.userId, fromPath.expectedUsername)); } catch { /* приватный режим */ }
    window.history.replaceState(null, '', '/');
    return fromPath;
  }
  try {
    const stored = sessionStorage.getItem('hc_pendingUserReference');
    const reference = stored ? parseStoredContactRoute(stored) : null;
    if (reference) return reference;
    sessionStorage.removeItem('hc_pendingUserReference');
  } catch { /* приватный режим */ }
  return null;
}

export default function App() {
  const { t } = useTranslation('app');
  const [sidebarWidth, setSidebarWidth] = useSidebarWidth();
  const [joinToken, setJoinToken] = useState<string | null>(() => readJoinToken());
  const [pendingContactQr, setPendingContactQr] = useState(() => readPendingContactQr());
  const [pendingUserReference, setPendingUserReference] = useState<ContactRouteReference | null>(() => readPendingUserReference());
  const [pendingUserBrief, setPendingUserBrief] = useState<UserBriefDto | null>(null);
  const [pendingUserError, setPendingUserError] = useState('');
  const [contactsVersion, setContactsVersion] = useState(0);
  const [user, setUser] = useState<UserDto | null>(null);
  useEffect(() => {
    if (!user?.id || (user.role !== 'admin' && user.role !== 'support')) return;
    const refresh = () => { void getSupportNotificationPrefs().catch(() => {}); };
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(timer);
  }, [user?.id, user?.role]);
  useEffect(() => {
    if (!user?.id) return;
    return startAutoDiagnostics((text) => submitSupportRequest(text, true));
  }, [user?.id]);
  const [chats, setChats] = useState<ChatSummary[]>([]);
  const [supportApplicant, setSupportApplicant] = useState<{ session: string | null; chatId: string; name: string } | null>(null);
  const [activeId, setActiveId] = useState<string | null>(() => localStorage.getItem('hc_activeChat'));
  // Активная комната открытого группового чата (null — общая), см. roomKey.
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const activeRoomRef = useRef<string | null>(null);
  activeRoomRef.current = activeRoomId;
  // Мобильный экран "список комнат" (см. RoomsColumn) — отдельный от "список чатов" и от
  // "сообщения", третий шаг в цепочке Чаты → Комнаты → Комната (см. отчёт пользователя). На
  // десктопе не влияет на вид: там список чатов, комнаты и лента показываются одновременно
  // (см. .layout.rooms-mode в theme.css — правила внутри медиазапроса для мобильных).
  const [roomsListOpen, setRoomsListOpen] = useState(false);
  const roomsListOpenRef = useRef(roomsListOpen);
  roomsListOpenRef.current = roomsListOpen;
  const [messages, setMessages] = useState<Map<string, MessageDto[]>>(new Map());
  const [statuses, setStatuses] = useState<Map<number, 'sent' | 'delivered' | 'read'>>(new Map());
  // "Печатает…": userId -> chatId, где он сейчас набирает. Индикатор в WS может потеряться
  // (обрыв связи прямо во время набора) — держим таймер автосброса как страховку.
  const [typingByChat, setTypingByChat] = useState<Map<string, Set<string>>>(new Map());
  const typingTimersRef = useRef<Map<string, number>>(new Map());
  const TYPING_TIMEOUT_MS = 6000;
  // Чаты, в которых достигнуто начало истории (loadOlder вернул пустую страницу).
  const startReachedRef = useRef<Set<string>>(new Set());
  const historyBeforeRef = useRef<Map<string, number | null>>(new Map());
  const [authed, setAuthed] = useState(isAuthenticated());
  const [alternateEndpoints, setAlternateEndpoints] = useState<string[]>(() => readCachedAltEndpoints(window.location.origin));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [updateAboutRequest, setUpdateAboutRequest] = useState(0);
  const [updateAboutPlatform, setUpdateAboutPlatform] = useState<UpdatePlatform>('android');
  const [feedbackComposerVersion, setFeedbackComposerVersion] = useState(0);
  // Раздел, с которого открывать настройки — клик по имени в меню сразу ведёт в «Аккаунт»
  // (см. отчёт пользователя "по клику на заголовок должен перемещаться в настройки - аккаунт").
  const [settingsInitialTab, setSettingsInitialTab] = useState<'account' | 'apps' | null>(null);
  const [adminOpen, setAdminOpen] = useState(false);
  // Меняется при любой правке папок в настройках — сигнал для ChatList перечитать список
  // (см. SettingsModal, вкладка «Папки», отчёт пользователя "работу с папками перенеси в настройки").
  const [foldersVersion, setFoldersVersion] = useState(0);
  // Заголовок вкладки из настроек приложения (см. отчёт пользователя: после смены имени в
  // админке title оставался "HomeChat" — в index.html он статичный). Сброшен при logout,
  // иначе следующий пользователь на том же устройстве увидел бы чужое имя.
  useEffect(() => {
    getAppSettings()
      .then((s) => { document.title = s.appName; })
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (!authed) document.title = 'HomeChat';
  }, [authed]);

  // Запасные origin кешируем только после входа. localStorage может быть запрещён, поэтому все
  // операции с кешем безопасны, а текущая страница продолжает работать без него.
  useEffect(() => {
    if (!authed) {
      setAlternateEndpoints([]);
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5_000);
    api<{ endpoints?: unknown }>('/api/v1/client/endpoints', { signal: controller.signal })
      .then((response) => {
        if (!cancelled) setAlternateEndpoints(cacheAltEndpoints(response, window.location.origin));
      })
      .catch(() => {
        if (!cancelled) setAlternateEndpoints(readCachedAltEndpoints(window.location.origin));
      })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [authed]);
  const activeIdRef = useRef<string | null>(null);
  activeIdRef.current = activeId;
  const messagesRef = useRef<Map<string, MessageDto[]>>(messages);
  messagesRef.current = messages;
  const userRef = useRef<UserDto | null>(null);
  userRef.current = user;
  const userLoadRef = useRef<Promise<UserDto> | null>(null);
  const ensureCurrentUser = useCallback(async (): Promise<UserDto> => {
    if (userRef.current) return userRef.current;
    let pending = userLoadRef.current;
    if (!pending) {
      const session = getAuthSession();
      pending = me().then((loaded) => {
        if (getAuthSession() !== session) throw new Error('Сессия изменилась');
        userRef.current = loaded;
        setUser(loaded);
        return loaded;
      });
      userLoadRef.current = pending;
    }
    try {
      return await pending;
    } finally {
      if (userLoadRef.current === pending) userLoadRef.current = null;
    }
  }, []);
  const chatsRef = useRef<ChatSummary[]>(chats);
  chatsRef.current = chats;
  const visibleMessages = useCallback((chatId: string, items: MessageDto[]) => {
    const chat = chatsRef.current.find((item) => item.id === chatId);
    const supportOperator = userRef.current?.role === 'admin' || userRef.current?.role === 'support';
    return hideTechnicalDiagnostics(items, chat?.isSupport ?? null, supportOperator);
  }, []);
  // Галки после перезагрузки страницы: события доставки/прочтения прошли, а в памяти статусов нет —
  // восстанавливаем из маркеров участников открытого чата (MemberDto.lastRead/lastDeliveredMessageId).
  const [receiptMembers, setReceiptMembers] = useState<{ chatId: string; members: MemberDto[] } | null>(null);
  useEffect(() => {
    if (!authed || !activeId) return;
    let cancelled = false;
    listMembers(activeId)
      .then((members) => {
        if (!cancelled) setReceiptMembers({ chatId: activeId, members });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [authed, activeId]);
  useEffect(() => {
    const myId = user?.id;
    if (!receiptMembers || !myId) return;
    const { chatId } = receiptMembers;
    const cursorsByRoom = readCursorsRef.current.get(chatId) ?? new Map<string, Map<string, number>>();
    for (const [key] of messages) {
      if (key !== chatId && !key.startsWith(chatId + ':')) continue;
      const roomId = key === chatId ? null : key.slice(chatId.length + 1);
      const scope = key;
      const cursors = cursorsByRoom.get(scope) ?? new Map<string, number>();
      for (const member of receiptMembers.members) {
        const readMarker = member.roomMarkers === undefined
          ? member.lastReadMessageId
          : member.roomMarkers.find((marker) => marker.roomId === roomId)?.lastReadMessageId;
        if (readMarker != null && readMarker > (cursors.get(member.user.id) ?? 0)) cursors.set(member.user.id, readMarker);
      }
      cursorsByRoom.set(scope, cursors);
    }
    readCursorsRef.current.set(chatId, cursorsByRoom);
    setStatuses((prev) => {
      let next = prev;
      for (const [key, list] of messages) {
        if (key !== chatId && !key.startsWith(chatId + ':')) continue;
        const roomId = key === chatId ? null : key.slice(chatId.length + 1);
        const t = receiptThresholds(receiptMembers.members, myId, user?.readReceiptsEnabled !== false, roomId);
        if (t.delivered <= 0) continue;
        for (const m of list) {
          if (m.senderId !== myId) continue;
          if (t.read > 0 && m.id <= t.read) next = upgradeStatus(next, m.id, 'read');
          else if (m.id <= t.delivered) next = upgradeStatus(next, m.id, 'delivered');
        }
      }
      return next;
    });
  }, [receiptMembers, messages, user?.id, user?.readReceiptsEnabled]);
  // openChat объявлен ниже (useCallback с зависимостью от messages, меняется при каждом
  // новом чате) — WS-обработчик входящих сообщений (см. offMsg ниже) подписывается один раз
  // на весь сеанс (deps: [authed]) и держал бы УСТАРЕВШУЮ версию openChat в замыкании, если
  // вызывать её напрямую. Тот же приём, что и activeIdRef/chatsRef/userRef выше.
  const openChatRef = useRef<(id: string) => void>(() => {});
  // Сколько сообщений было непрочитано НА МОМЕНТ открытия чата (см. openChat) — ChatView
  // использует это, чтобы поставить начальный скролл на первое непрочитанное, а не в самый
  // конец, если чат открыт не "с нуля" (см. отчёт пользователя "если прочитано до какого-то
  // сообщения, чат должен открываться на этом же месте"). Ref, а не state — значение нужно
  // ТОЛЬКО синхронно на момент рендера с новым activeId, дальнейшие изменения unreadCount
  // (бейдж сбрасывается сразу же) не должны на него влиять.
  const chatUnreadOnOpenRef = useRef<Map<string, number>>(new Map());
  // Прочтение в группе: синие галочки не у "кто-то один прочитал" (как в личном чате), а
  // только когда сообщение дочитали ВСЕ участники — иначе для группы из 10 человек статус
  // синел бы уже после первого прочтения, что вводит в заблуждение насчёт остальных.
  // chatId -> id всех участников (включая себя) — догружаем лениво по первому read-событию
  // в группе, которой ещё нет в кэше; членство меняется редко, инвалидацией не занимаемся.
  const groupMembersRef = useRef<Map<string, string[]>>(new Map());
  const groupMembersLoadingRef = useRef<Set<string>>(new Set());
  // chatId -> userId -> последний прочитанный этим участником id сообщения.
  const readCursorsRef = useRef<Map<string, Map<string, Map<string, number>>>>(new Map());

  // ---------- звонки (глобально, не привязаны к открытому чату) ----------
  const [call, setCall] = useState<{ mode: CallMode; chatId: string } | null>(null);
  const ringbackStopRef = useOutgoingRingback(call?.mode === 'outgoing');
  // Последняя ошибка доступа к камере/микрофону из звонка — для баннера «Нужен доступ».
  const [mediaFailure, setMediaFailure] = useState<MediaFailure | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [preparationDevices, setPreparationDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedPreparationCameraId, setSelectedPreparationCameraId] = useState('');
  const [preparationError, setPreparationError] = useState('');
  const preparationStreamRef = useRef<MediaStream | null>(null);
  const sessionRef = useRef<RtcSession | RtcV2Session | null>(null);
  const acceptGateRef = useRef(new AsyncAttemptGate());
  const endedV2CallIdsRef = useRef(new EndedCallIdRegistry());
  const wasActiveRef = useRef(false);
  const callVideoRef = useRef(false);
  const startTimeRef = useRef(0);
  const ringStopRef = useRef<(() => void) | null>(null);
  const [callMediaWarning, setCallMediaWarning] = useState('');
  const callWarningTimerRef = useRef<number | null>(null);
  // Мьют микрофона/камеры (см. отчёт пользователя "добавить кнопку выключить звук и
  // отображать всем что звук выключен/включен... в видеозвонках также выключение камеры") —
  // локально (своя кнопка) и то, что прислал собеседник о СВОЁМ состоянии (см. rtc.ts
  // toggleMic/toggleCamera и RtcSignal.kind/muted).
  const [localMuted, setLocalMuted] = useState({ audio: false, video: false });
  const [remoteMuted, setRemoteMuted] = useState({ audio: false, video: false });
  const [localHeld, setLocalHeld] = useState(false);
  const [remoteHeld, setRemoteHeld] = useState(false);

  // ---------- групповые звонки (mesh, см. rtcGroup.ts) — отдельное состояние от 1:1 выше,
  // чтобы не переплетать разные модели (там ринг конкретному человеку, тут "войти в комнату
  // разговора этого чата"), см. отчёт пользователя "групповые звонки нужно попробовать
  // реализовать". ----------
  const [groupCall, setGroupCall] = useState<{ chatId: string; mode: 'incoming' | 'connecting' | 'active'; isCreator: boolean } | null>(null);
  const [groupLocalStream, setGroupLocalStream] = useState<MediaStream | null>(null);
  const [groupStreams, setGroupStreams] = useState<Map<string, MediaStream>>(new Map());
  const [groupRoster, setGroupRoster] = useState<string[]>([]);
  const groupSessionRef = useRef<GroupCallSession | GroupCallV2Session | null>(null);
  // Мьют — свой (кнопка) и по каждому собеседнику (см. rtcGroup.ts toggleMic/toggleCamera).
  const [groupLocalMuted, setGroupLocalMuted] = useState({ audio: false, video: false });
  const [groupPeerMuted, setGroupPeerMuted] = useState<Map<string, { audio: boolean; video: boolean }>>(new Map());

  const initialChatsCheckedRef = useRef(false);
  const refreshChats = useCallback(async (signal?: AbortSignal) => {
    try {
      // Чат-флаги нельзя санитизировать без роли/ID: загрузить identity перед списком чатов.
      const activeUser = await ensureCurrentUser();
      const received = await listChats();
      const list = await hideTechnicalSupportMessages(
        received,
        activeUser?.id ?? null,
        activeUser?.role === 'admin' || activeUser?.role === 'support',
        history,
      );
      if (signal?.aborted) return;
      chatsRef.current = list;
      setChats(list);
      // Наблюдаем ВСЕ чаты (не только открытый) — иначе звонки/сообщения
      // из неоткрытых чатов не долетают по WS вообще.
      for (const c of list) realtime.watchChat(c.id);
      // Первый загруженный список — истина о доступе. Подписка на чат, которого в нём нет (исключили, чат
      // удалён, в localStorage остался чужой hc_activeChat), заставляет сервер рвать WS на SUBSCRIBE, а клиент
      // переподписывается на каждом коннекте — бесконечный цикл «Соединение…». Только первый список: позже
      // свежесозданный/только что открытый чат может ещё не попасть в уже запрошенный список.
      if (!initialChatsCheckedRef.current) {
        initialChatsCheckedRef.current = true;
        const ids = new Set(list.map((c) => c.id));
        for (const id of realtime.watchedChatIds()) if (!ids.has(id)) realtime.unwatchChat(id);
        const staleActive = activeIdRef.current;
        if (staleActive && !ids.has(staleActive)) {
          localStorage.removeItem('hc_activeChat');
          setActiveId(null);
          activeIdRef.current = null;
          activeRoomRef.current = null;
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401 && !isAuthenticated()) {
        userRef.current = null;
        setUser(null);
        setAuthed(false);
      }
      if (signal) throw error;
      /* обычное обновление списка не критично; resync должен повториться при ошибке */
    }
  }, [ensureCurrentUser]);

  // Данные служебного сообщения о звонке: тип + длительность + время
  const callEndData = () => {
    const kind = callVideoRef.current ? 'video' : 'audio';
    const callTime = new Date(startTimeRef.current).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (!wasActiveRef.current) {
      // Текст служебного сообщения сохраняется на сервере и виден собеседнику — не локализуется.
      return { text: `Пропущенный звонок · ${callTime}`, call: kind, missed: true }; // i18n-ok
    }
    const sec = (Date.now() - startTimeRef.current) / 1000;
    return {
      text: `${callVideoRef.current ? 'Видеозвонок' : 'Звонок'} · ${formatDuration(sec)}`, // i18n-ok
      call: kind,
      missed: false,
    };
  };

  const sendServiceFor = useCallback((chatId: string) => {
    realtime
      .send(chatId, {
        clientMsgId: randomUUID(),
        type: 99,
        payload: { alg: 'plaintext', data: callEndData() },
      })
      .catch(() => {});
  }, []);

  // Не смогли получить видео с камеры для звонка (permission/занята/нет устройства) —
  // раньше это тихо откатывалось на аудио без объяснений, теперь показываем причину.
  // Звонок состоялся — прежняя ошибка доступа неактуальна.
  useEffect(() => {
    if (call?.mode === 'active') setMediaFailure(null);
  }, [call?.mode]);

  const showCallMediaWarning = (err: unknown) => {
    const name = err instanceof DOMException ? err.name : 'Error';
    setMediaFailure({ kind: 'camera', name });
    const reason: Record<string, string> = {
      NotAllowedError: t('cameraPermissionDenied'),
      NotFoundError: t('cameraNotFound'),
      NotReadableError: t('cameraBusy'),
      OverconstrainedError: t('cameraUnsupportedMode'),
    };
    setCallMediaWarning(t('videoUnavailable', { reason: reason[name] ?? name }));
    if (callWarningTimerRef.current) clearTimeout(callWarningTimerRef.current);
    callWarningTimerRef.current = window.setTimeout(() => setCallMediaWarning(''), 8000);
  };

  // Не удалось получить даже аудио (микрофон тоже недоступен, не только камера) — звонок
  // вообще не может начаться. Раньше это тоже тихо проглатывалось (catch { destroy(); return; }),
  // пользователь не понимал, почему нажатие на "позвонить" ни к чему не приводит (см. отчёт
  // пользователя "не запрашивает права ... на работу с камерой и микрофоном").
  const showCallFailedWarning = (err: unknown) => {
    const name = err instanceof DOMException ? err.name : 'Error';
    setMediaFailure({ kind: 'microphone', name });
    const reason: Record<string, string> = {
      NotAllowedError: t('microphonePermissionDenied'),
      NotFoundError: t('microphoneNotFound'),
      NotReadableError: t('microphoneBusy'),
    };
    setCallMediaWarning(t('callFailed', { reason: reason[name] ?? name }));
    if (callWarningTimerRef.current) clearTimeout(callWarningTimerRef.current);
    callWarningTimerRef.current = window.setTimeout(() => setCallMediaWarning(''), 8000);
  };

  const cleanupCall = useCallback(() => {
    ringbackStopRef.current?.();
    ringStopRef.current?.();
    ringStopRef.current = null;
    const ending = sessionRef.current;
    if (ending instanceof RtcV2Session && ending.machine.callId) {
      endedV2CallIdsRef.current.remember(ending.chatId, ending.machine.callId);
    }
    acceptGateRef.current.cancel();
    ending?.destroy();
    sessionRef.current = null;
    wasActiveRef.current = false;
    preparationStreamRef.current?.getTracks().forEach((track) => track.stop());
    preparationStreamRef.current = null;
    setPreparationDevices([]);
    setSelectedPreparationCameraId('');
    setPreparationError('');
    setRemoteStream(null);
    setLocalStream(null);
    setCall(null);
    setLocalMuted({ audio: false, video: false });
    setRemoteMuted({ audio: false, video: false });
    setLocalHeld(false);
    setRemoteHeld(false);
  }, [ringbackStopRef]);

  // Звонки 1:1 по v2 по умолчанию; входящие v1 и явный откат сохраняют совместимость.
  const showCallNotice = (text: string) => {
    setCallMediaWarning(text);
    if (callWarningTimerRef.current) clearTimeout(callWarningTimerRef.current);
    callWarningTimerRef.current = window.setTimeout(() => setCallMediaWarning(''), 8000);
  };

  const v2Notice = (info: { reason: string; detail?: string; local: boolean; role: string | null }) => {
    const rejected: Record<string, string> = {
      chat_busy: 'callV2RejectedChatBusy',
      limit: 'callV2RejectedLimit',
      rate_limited: 'callV2RejectedRateLimited',
      forbidden: 'callV2RejectedForbidden',
    };
    if (info.detail === 'unsupported_peer') return showCallNotice(t('callV2Unsupported'));
    if (info.detail === 'lost') return showCallNotice(t('callV2Lost'));
    if (info.detail && rejected[info.detail]) return showCallNotice(t(rejected[info.detail]));
    if (info.local) return;
    const caller = info.role === 'caller';
    switch (info.reason) {
      case 'declined': if (caller) showCallNotice(t('callV2Declined')); break;
      case 'busy': if (caller) showCallNotice(t('callV2Busy')); break;
      case 'timeout': showCallNotice(t(caller ? 'callV2NoAnswer' : 'callV2Missed')); break;
      case 'cancelled': if (!caller) showCallNotice(t('callV2Missed')); break;
      case 'ice_failed': showCallNotice(t('callV2IceFailed')); break;
      case 'error': showCallNotice(t('callV2Error')); break;
    }
  };

  const v2Handlers = (chatId: string): RtcV2Handlers => ({
    onRemoteStream: setRemoteStream,
    onRemoteMuteState: (kind, muted) => setRemoteMuted((prev) => ({ ...prev, [kind]: muted })),
    onRemoteHold: (held) => setRemoteHeld(held),
    onPhase: (phase, m) => {
      // Тип входящего может измениться при встречном вызове: UI и подготовка используют один флаг.
      callVideoRef.current = m.video;
      if (phase === 'creating' || phase === 'ringing_out') {
        setCall({ mode: 'outgoing', chatId });
      } else if (phase === 'ringing_in') {
        setLocalStream(null);
        setCall({ mode: 'incoming', chatId });
        if (!ringStopRef.current) ringStopRef.current = startRingtone();
      } else if (phase === 'accepting' || phase === 'connecting') {
        setCall({ mode: 'connecting', chatId });
      } else if (phase === 'active') {
        wasActiveRef.current = true;
        setCall({ mode: 'active', chatId });
      }
    },
    onLegacyFallback: (stream, video, deadlineAt) => {
      const current = sessionRef.current;
      if (!(current instanceof RtcV2Session) || current.chatId !== chatId || !current.isDestroyed) {
        stream?.getTracks().forEach((track) => track.stop());
        return;
      }
      sessionRef.current = null;
      void startLegacyPersonalCall(video, chatId, stream, deadlineAt);
    },
    onFinished: (info) => {
      // Служебное сообщение v2 создаёт серверный CallFinishedEvent.
      v2Notice(info);
      cleanupCall();
    },
  });

  const beginIncomingV2 = (chatId: string, callId: string, video: boolean, deadlineAt: number) => {
    if (!user) return;
    callVideoRef.current = video;
    startTimeRef.current = Date.now();
    const s = new RtcV2Session(chatId, user.id, v2Handlers(chatId));
    sessionRef.current = s;
    s.receiveIncoming(callId, video, deadlineAt);
  };

  const handleV2Event = (chatId: string, ev: V2Event) => {
    if (!user) return;
    const cur = sessionRef.current;
    if (cur instanceof RtcV2Session && cur.chatId === chatId) {
      cur.onEvent(ev);
      return;
    }
    if (ev.type !== 'incoming' || ev.fromUserId === user.id || typeof ev.callId !== 'string') return;
    if (!mayStartIncomingV2Call(chatId, ev.callId, endedV2CallIdsRef.current)) return;
    if (cur || groupSessionRef.current) {
      replyUnavailable(chatId, ev.callId); // уже в другом звонке — «занят» (calls.md §5.5)
      return;
    }
    beginIncomingV2(chatId, ev.callId, ev.video === true, typeof ev.deadlineAt === 'number' ? ev.deadlineAt : 0);
  };

  // Всегда актуальные замыкания для эффектов ниже (они не должны пересоздаваться на каждый рендер).
  const v2ActionsRef = useRef({ handleV2Event, beginIncomingV2 });
  v2ActionsRef.current = { handleV2Event, beginIncomingV2 };
  const groupV2EventHandlerRef = useRef<((chatId: string, event: V2Event) => void) | null>(null);

  // Сверка с сервером при возврате в приложение и переподключении WS: потерянный `ended` не должен
  // оставлять «зависший» экран звонка; входящий, пришедший пока вкладка спала (push), показывается.
  useEffect(() => {
    if (!user) return;
    const check = () => {
      if (!rtcV2Enabled()) return;
      const cur = sessionRef.current;
      if (cur instanceof RtcV2Session) {
        void cur.refreshSnapshot();
        return;
      }
      const chatId = activeIdRef.current;
      const chat = chatsRef.current.find((c) => c.id === chatId);
      if (cur || groupSessionRef.current || !chatId || chat?.type !== 1) return;
      fetchCallSnapshot(chatId)
        .then((snap) => {
          if (!snap || snap.state !== 'ringing' || snap.fromUserId === user.id) return;
          if (snap.deadlineAt && snap.deadlineAt < Date.now()) return;
          if (sessionRef.current || groupSessionRef.current) return;
          v2ActionsRef.current.beginIncomingV2(chatId, snap.callId, snap.video, snap.deadlineAt ?? 0);
        })
        .catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    const unsubStatus = realtime.onStatus((connected) => {
      if (!connected) return;
      const active = sessionRef.current;
      if (active instanceof RtcSession || active instanceof RtcV2Session) active.announceHoldState();
      check();
    });
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      unsubStatus();
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const unsubStatus = realtime.onStatus((connected) => {
      if (!connected) return;
      const active = sessionRef.current;
      if (active instanceof RtcSession) active.onSignalingReconnected();
    });
    return unsubStatus;
  }, [user]);

  // Входящие звонки: подписка на RTC-сигналы ВСЕХ наблюдаемых чатов.
  useEffect(() => {
    if (!user) return;
    const unsub = realtime.onRtc((chatId, raw) => {
      if (isV2Event(raw)) {
        if (raw.callKind === 'group') {
          if (!rtcGroupV2Enabled()) return;
          groupV2EventHandlerRef.current?.(chatId, raw);
          return;
        }
        v2ActionsRef.current.handleV2Event(chatId, raw);
        return;
      }
      const sig = raw as RtcSignal;
      if (sig.fromUserId === user.id && sig.type !== 'incoming_closed' && sig.type !== 'end') return;
      if (sig.type === 'call' && !sessionRef.current) {
        // Медиа НЕ запрашиваем до согласия пользователя
        callVideoRef.current = !!sig.video;
        startTimeRef.current = Date.now();
        const s = new RtcSession(chatId, user.id, {
          onRemoteStream: setRemoteStream,
          onRemoteMuteState: (kind, muted) => setRemoteMuted((prev) => ({ ...prev, [kind]: muted })),
          onRemoteHold: setRemoteHeld,
          onRemoteEnd: () => {
            sendServiceFor(chatId);
            cleanupCall();
          },
          onReject: () => {
            sendServiceFor(chatId);
            cleanupCall();
          },
          onConnected: () => {
            wasActiveRef.current = true;
            setCall({ mode: 'active', chatId });
          },
        });
        sessionRef.current = s;
        setCall({ mode: 'incoming', chatId });
        ringStopRef.current = startRingtone();
        s.onSignal(sig); // setRemoteDescription(offer)
      } else if (sessionRef.current instanceof RtcSession && sessionRef.current.chatId === chatId) {
        sessionRef.current.onSignal(sig);
      }
    });
    return unsub;
  }, [user, cleanupCall, sendServiceFor]);

  const startPersonalCallV2 = async (video: boolean) => {
    const chatId = activeIdRef.current;
    if (!user || !chatId || sessionRef.current || groupSessionRef.current) return;
    callVideoRef.current = video;
    startTimeRef.current = Date.now();
    const s = new RtcV2Session(chatId, user.id, v2Handlers(chatId));
    sessionRef.current = s; // сразу: защита от повторного нажатия, пока запрашиваются права
    try {
      await s.startWithMedia(true, video);
    } catch (err) {
      if (video) showCallMediaWarning(err);
      try {
        await s.startWithMedia(true, false);
        video = false;
      } catch (err2) {
        showCallFailedWarning(err2);
        s.destroy();
        sessionRef.current = null;
        return;
      }
    }
    if (sessionRef.current !== s || s.isDestroyed) { // пока ждали права, звонок сброшен
      s.destroy();
      return;
    }
    setLocalStream(s.localStream ?? null);
    s.startOutgoing(video); // SDP создаётся после `accepted`; фазы ведёт onPhase
  };

  const startPersonalCall = async (video: boolean) => {
    if (rtcV2Enabled()) return startPersonalCallV2(video);
    return startLegacyPersonalCall(video, activeIdRef.current);
  };

  const startLegacyPersonalCall = async (video: boolean, chatId: string | null, stream?: MediaStream, deadlineAt?: number) => {
    if (!user || !chatId || sessionRef.current || groupSessionRef.current) {
      stream?.getTracks().forEach((track) => track.stop());
      return;
    }
    callVideoRef.current = video;
    startTimeRef.current = Date.now();
    const s = new RtcSession(chatId, user.id, {
      onAnswer: () => setCall((current) => current?.mode === 'outgoing' && current.chatId === chatId
        ? { mode: 'connecting', chatId } : current),
      onRemoteStream: setRemoteStream,
      onRemoteEnd: () => {
        sendServiceFor(chatId);
        cleanupCall();
      },
      onReject: () => {
        sendServiceFor(chatId);
        cleanupCall();
      },
      onConnected: () => {
        wasActiveRef.current = true;
        setCall({ mode: 'active', chatId });
      },
      onRemoteMuteState: (kind, muted) => setRemoteMuted((prev) => ({ ...prev, [kind]: muted })),
      onRemoteHold: setRemoteHeld,
    });
    sessionRef.current = s;
    try {
      if (stream) await s.startWithStream(stream, stream.getAudioTracks().some((track) => track.enabled), stream.getVideoTracks().some((track) => track.enabled));
      else await s.startWithMedia(true, video);
    } catch (err) {
      // камеры нет — пробуем аудио
      if (video) showCallMediaWarning(err);
      try {
        await s.startWithMedia(true, false);
        video = false;
      } catch (err2) {
        showCallFailedWarning(err2);
        s.destroy();
        if (sessionRef.current === s) cleanupCall();
        return;
      }
    }
    callVideoRef.current = video;
    setLocalStream(s.localStream ?? null);
    if (sessionRef.current !== s || s.isDestroyed) return;
    try { await s.call(video, deadlineAt); }
    catch (error) {
      showCallFailedWarning(error);
      s.destroy();
      if (sessionRef.current === s) cleanupCall();
      return;
    }
    if (sessionRef.current !== s || s.isDestroyed) return;
    setCall({ mode: 'outgoing', chatId });
  };

  const prepareVideoAnswer = async (cameraId = '') => {
    const currentCall = call;
    const s = sessionRef.current;
    if (!s || !currentCall) return;
    if (!callVideoRef.current) {
      await acceptCall();
      return;
    }
    setPreparationError('');
    preparationStreamRef.current?.getTracks().forEach((track) => track.stop());
    preparationStreamRef.current = null;
    setLocalStream(null);
    setCall({ ...currentCall, mode: 'preparing' });
    if (currentCall.mode !== 'preparing') setLocalMuted({ audio: false, video: false });
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: cameraId ? { deviceId: { exact: cameraId } } : true,
      });
    } catch (error) {
      setPreparationError(error instanceof DOMException ? error.name : 'Camera unavailable');
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch {
        // Без устройств можно продолжить звонок, получая только удалённые дорожки.
        stream = new MediaStream();
      }
    }
    if (sessionRef.current !== s || s.isDestroyed) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    preparationStreamRef.current = stream;
    setLocalStream(stream);
    const audioMuted = localMuted.audio || stream.getAudioTracks().length === 0;
    const cameraMuted = localMuted.video || stream.getVideoTracks().length === 0;
    setLocalMuted({ audio: audioMuted, video: cameraMuted });
    stream.getAudioTracks().forEach((track) => { track.enabled = !audioMuted; });
    stream.getVideoTracks().forEach((track) => { track.enabled = !cameraMuted; });
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      setPreparationDevices(cameras);
      setSelectedPreparationCameraId(cameraId || cameras[0]?.deviceId || '');
    } catch {
      setPreparationDevices([]);
    }
  };

  const acceptCall = async () => {
    const s = sessionRef.current;
    if (!s || !call) return;
    ringStopRef.current?.();
    ringStopRef.current = null;
    // По ответу на звонок переключаемся на чат, из которого он пришёл.
    if (call.chatId !== activeIdRef.current) {
      openChat(call.chatId);
    }
    const acceptAttempt = s instanceof RtcV2Session ? acceptGateRef.current.begin() : null;
    if (s instanceof RtcV2Session && acceptAttempt === null) return; // уже запрашиваем media для этой попытки
    const releaseAcceptAttempt = () => {
      if (acceptAttempt !== null) acceptGateRef.current.finish(acceptAttempt);
    };
    let video = s instanceof RtcV2Session ? s.machine.video : callVideoRef.current;
    const preparedStream = call.mode === 'preparing' ? preparationStreamRef.current : null;
    try {
      if (preparedStream) {
        preparationStreamRef.current = null;
        await s.startWithStream(preparedStream, !localMuted.audio, !localMuted.video);
      } else {
        await s.startWithMedia(true, video);
      }
      if (sessionRef.current !== s || s.isDestroyed) {
        releaseAcceptAttempt();
        s.destroy();
        return;
      }
    } catch (err) {
      if (sessionRef.current !== s || s.isDestroyed) { releaseAcceptAttempt(); s.destroy(); return; }
      if (video) showCallMediaWarning(err);
      try {
        await s.startWithMedia(true, false);
        if (sessionRef.current !== s || s.isDestroyed) {
          releaseAcceptAttempt();
          s.destroy();
          return;
        }
        video = false;
      } catch (err2) {
        if (sessionRef.current !== s || s.isDestroyed) { releaseAcceptAttempt(); s.destroy(); return; }
        releaseAcceptAttempt();
        showCallFailedWarning(err2);
        // v2: сообщаем серверу, что эта сессия принять не может (иначе звонящий ждёт таймаута)
        if (s instanceof RtcV2Session && s.machine.callId) replyUnavailable(s.chatId, s.machine.callId);
        cleanupCall();
        return;
      }
    }
    releaseAcceptAttempt();
    if (s.isDestroyed || sessionRef.current !== s) return;
    callVideoRef.current = video; // фолбэк на аудио — иначе UI всё ещё думает, что видео
    setLocalStream(s.localStream ?? null);
    if (s instanceof RtcV2Session) {
      s.accept(); // дальше: accepted → offer от звонящего → answer (rtcV2.ts)
      s.announceInitialMuteState();
      return;
    }
    try {
      await s.answer();
      if (s.isDestroyed || sessionRef.current !== s) return;
      s.announceInitialMuteState();
      setCall({ mode: 'connecting', chatId: call.chatId }); // 'active' поставит onConnected
    } catch {
      if (sessionRef.current === s) cleanupCall();
    }
  };
  const rejectCall = () => {
    const s = sessionRef.current;
    if (s instanceof RtcV2Session) {
      s.reject(); // onFinished закроет экран
      return;
    }
    if (call) sendServiceFor(call.chatId);
    sessionRef.current?.reject();
    cleanupCall();
  };
  const hangup = () => {
    ringbackStopRef.current?.();
    const s = sessionRef.current;
    if (s instanceof RtcV2Session) {
      s.hangup(); // onFinished закроет экран; если callId ещё нет — завершим по ответу сервера
      // Сервер не ответил (нет связи) — не оставляем пользователя на зависшем экране.
      window.setTimeout(() => { if (sessionRef.current === s) cleanupCall(); }, 5000);
      return;
    }
    if (call) sendServiceFor(call.chatId);
    sessionRef.current?.hangup();
    cleanupCall();
  };

  const cleanupGroupCall = useCallback(() => {
    groupSessionRef.current = null;
    setGroupCall(null);
    setGroupLocalStream(null);
    setGroupStreams(new Map());
    setGroupRoster([]);
    setGroupLocalMuted({ audio: false, video: false });
    setGroupPeerMuted(new Map());
  }, []);

  const groupV2Handlers = (chatId: string): GroupCallV2Handlers => ({
    onPhase: (phase) => {
      if (phase === 'idle' || phase === 'ended') return;
      const session = groupSessionRef.current;
      setGroupCall({
        chatId,
        mode: phase,
        isCreator: session instanceof GroupCallV2Session && session.isCreator,
      });
    },
    onRoster: (members) => setGroupRoster(members.filter((member) => member.userId !== user?.id).map((member) => member.userId)),
    onRemoteStream: (userId, stream) => setGroupStreams((prev) => new Map(prev).set(userId, stream)),
    onPeerLeft: (userId) => {
      setGroupStreams((prev) => {
        if (!prev.has(userId)) return prev;
        const next = new Map(prev);
        next.delete(userId);
        return next;
      });
      setGroupRoster((prev) => prev.filter((id) => id !== userId));
      setGroupPeerMuted((prev) => {
        if (!prev.has(userId)) return prev;
        const next = new Map(prev);
        next.delete(userId);
        return next;
      });
    },
    onPeerMuteState: (userId, kind, muted) => {
      setGroupPeerMuted((prev) => {
        const next = new Map(prev);
        const cur = next.get(userId) ?? { audio: false, video: false };
        next.set(userId, { ...cur, [kind]: muted });
        return next;
      });
    },
    onJoinRejected: (reason) => showCallNotice(t(reason === 'full' ? 'groupCallFull' : 'groupCallJoinElsewhere')),
    onFinished: () => cleanupGroupCall(),
  });

  const handleGroupV2Event = (chatId: string, event: V2Event) => {
    if (!user) return;
    const current = groupSessionRef.current;
    if (current instanceof GroupCallV2Session && current.chatId === chatId) {
      current.onEvent(event);
      return;
    }
    if (event.type !== 'incoming' || event.fromUserId === user.id || typeof event.callId !== 'string') return;
    if (sessionRef.current || current) {
      realtime.publish(rtcApp(chatId), { v: 2, callKind: 'group', type: 'decline', callId: event.callId });
      return;
    }
    const session = new GroupCallV2Session(chatId, user.id, groupV2Handlers(chatId));
    groupSessionRef.current = session;
    session.receiveIncoming(event.callId, event.video === true,
      typeof event.deadlineAt === 'number' ? event.deadlineAt : 0);
  };
  groupV2EventHandlerRef.current = handleGroupV2Event;

  const startGroupCallV2 = async (video: boolean) => {
    const chatId = activeIdRef.current;
    if (!user || !chatId || sessionRef.current || groupSessionRef.current) return;
    const session = new GroupCallV2Session(chatId, user.id, groupV2Handlers(chatId));
    groupSessionRef.current = session;
    try {
      await session.startWithMedia(true, video);
    } catch (error) {
      if (session.isDestroyed || groupSessionRef.current !== session) return;
      if (video) showCallMediaWarning(error);
      try {
        await session.startWithMedia(true, false);
        video = false;
      } catch (audioError) {
        if (session.isDestroyed || groupSessionRef.current !== session) return;
        showCallFailedWarning(audioError);
        session.destroy();
        return;
      }
    }
    if (session.isDestroyed || groupSessionRef.current !== session) { session.destroy(); return; }
    setGroupLocalStream(session.localStream ?? null);
    session.startOutgoing(video, activeRoomRef.current);
  };

  const startGroupCallV1 = async (video: boolean) => {
    const chatId = activeIdRef.current;
    if (!user || !chatId || sessionRef.current || groupSessionRef.current) return;
    const s = new GroupCallSession(chatId, user.id, {
      onRemoteStream: (peerId, stream) => setGroupStreams((prev) => new Map(prev).set(peerId, stream)),
      onPeerLeft: (peerId) => {
        setGroupStreams((prev) => {
          if (!prev.has(peerId)) return prev;
          const next = new Map(prev);
          next.delete(peerId);
          return next;
        });
        setGroupRoster((prev) => prev.filter((id) => id !== peerId));
        setGroupPeerMuted((prev) => {
          if (!prev.has(peerId)) return prev;
          const next = new Map(prev);
          next.delete(peerId);
          return next;
        });
      },
      onRoster: (members) => setGroupRoster(members),
      onPeerMuteState: (peerId, kind, muted) => {
        setGroupPeerMuted((prev) => {
          const next = new Map(prev);
          const cur = next.get(peerId) ?? { audio: false, video: false };
          next.set(peerId, { ...cur, [kind]: muted });
          return next;
        });
      },
    });
    try {
      await s.startWithMedia(true, video);
    } catch (err) {
      if (video) showCallMediaWarning(err);
      try {
        await s.startWithMedia(true, false);
      } catch (err2) {
        showCallFailedWarning(err2);
        s.destroy();
        return;
      }
    }
    setGroupLocalStream(s.localStream ?? null);
    groupSessionRef.current = s;
    setGroupCall({ chatId, mode: 'active', isCreator: false });
    s.join();
  };

  const startGroupCall = async (video: boolean) => {
    if (rtcGroupV2Enabled()) return startGroupCallV2(video);
    return startGroupCallV1(video);
  };

  const joinGroupCallV2 = async () => {
    const session = groupSessionRef.current;
    if (!(session instanceof GroupCallV2Session) || !session.isIncoming) return;
    const video = session.wantsVideo;
    try {
      await session.startWithMedia(true, video);
    } catch (error) {
      if (session.isDestroyed || groupSessionRef.current !== session) return;
      if (video) showCallMediaWarning(error);
      try {
        await session.startWithMedia(true, false);
      } catch (audioError) {
        if (session.isDestroyed || groupSessionRef.current !== session) return;
        showCallFailedWarning(audioError);
        session.decline();
        return;
      }
    }
    if (session.isDestroyed || groupSessionRef.current !== session) { session.destroy(); return; }
    setGroupLocalStream(session.localStream ?? null);
    session.join();
  };

  const declineGroupCallV2 = () => {
    const session = groupSessionRef.current;
    if (session instanceof GroupCallV2Session && session.isIncoming) session.decline();
  };

  const leaveGroupCall = () => {
    const session = groupSessionRef.current;
    if (session instanceof GroupCallV2Session) session.leaveOrEnd();
    else session?.leave();
    cleanupGroupCall();
  };

  const toggleMic = () => {
    const muted = sessionRef.current?.toggleMic();
    if (muted != null) setLocalMuted((prev) => ({ ...prev, audio: muted }));
  };
  const toggleCamera = () => {
    const muted = sessionRef.current?.toggleCamera();
    if (muted != null) setLocalMuted((prev) => ({ ...prev, video: muted }));
  };
  const togglePreparationMic = () => {
    setLocalMuted((prev) => {
      const muted = !prev.audio;
      preparationStreamRef.current?.getAudioTracks().forEach((track) => { track.enabled = !muted; });
      return { ...prev, audio: muted };
    });
  };
  const togglePreparationCamera = () => {
    setLocalMuted((prev) => {
      const muted = !prev.video;
      preparationStreamRef.current?.getVideoTracks().forEach((track) => { track.enabled = !muted; });
      return { ...prev, video: muted };
    });
  };
  const changePreparationCamera = (deviceId: string) => {
    setSelectedPreparationCameraId(deviceId);
    void prepareVideoAnswer(deviceId);
  };
  const toggleHold = () => {
    const active = sessionRef.current;
    if (!(active instanceof RtcSession) && !(active instanceof RtcV2Session)) return;
    active.setHeld(!active.isHeld);
    setLocalHeld(active.isHeld);
  };
  const toggleGroupMic = () => {
    const muted = groupSessionRef.current?.toggleMic();
    if (muted != null) setGroupLocalMuted((prev) => ({ ...prev, audio: muted }));
  };
  const toggleGroupCamera = () => {
    const muted = groupSessionRef.current?.toggleCamera();
    if (muted != null) setGroupLocalMuted((prev) => ({ ...prev, video: muted }));
  };

  /** Кнопки звонка в шапке чата ведут сюда независимо от типа чата — дальше решаем, какая
   *  модель звонка нужна (см. отчёт пользователя "групповые звонки нужно попробовать
   *  реализовать": для групп нет "звонка конкретному человеку", только общая комната). */
  const startCall = (video: boolean) => {
    const chat = chatsRef.current.find((c) => c.id === activeIdRef.current);
    if (chat?.type === 2) startGroupCall(video);
    else startPersonalCall(video);
  };

  // Групповые RTC-сигналы (join/leave/offer/answer/ice/roster, см. rtcGroup.ts) — тот же relay,
  // что и у 1:1 (см. подписку выше), но отдельным обработчиком: типы сообщений разные,
  // конфликтов нет (см. RtcWs), а активная групповая сессия — максимум одна за раз.
  useEffect(() => {
    if (!user) return;
    const unsub = realtime.onRtc((chatId, raw) => {
      const s = groupSessionRef.current;
      if (!(s instanceof GroupCallSession) || s.chatId !== chatId) return;
      s.onSignal(raw as GroupRtcSignal);
    });
    return unsub;
  }, [user]);

  const setTyping = useCallback((chatId: string, userId: string, isTyping: boolean) => {
    const timerKey = chatId + ':' + userId;
    const timer = typingTimersRef.current.get(timerKey);
    if (timer) window.clearTimeout(timer);
    typingTimersRef.current.delete(timerKey);
    setTypingByChat((prev) => {
      const next = new Map(prev);
      const set = new Set(next.get(chatId) ?? []);
      if (isTyping) set.add(userId);
      else set.delete(userId);
      next.set(chatId, set);
      return next;
    });
    if (isTyping) {
      typingTimersRef.current.set(
        timerKey,
        window.setTimeout(() => setTyping(chatId, userId, false), TYPING_TIMEOUT_MS),
      );
    }
  }, []);

  // События чатов (редактирование/удаление/прочтение) — для ВСЕХ наблюдаемых чатов.
  useEffect(() => {
    const unsub = realtime.onEvent((chatId, raw) => {
      const ev = raw as { type: string; message?: MessageDto; message_id?: number; user_id?: string; room_id?: string | null; is_typing?: boolean; title?: string };
      if (ev.type === 'typing' && ev.user_id && ev.user_id !== userRef.current?.id) {
        setTyping(chatId, ev.user_id, Boolean(ev.is_typing));
      } else if (ev.type === 'message_edited' && ev.message) {
        const editedKey = roomKey(ev.message.chatId, ev.message.roomId);
        setMessages((prev) => {
          const next = new Map(prev);
          const list = (next.get(editedKey) ?? []).map((m) =>
            m.id === ev.message!.id ? ev.message! : m,
          );
          next.set(editedKey, list);
          return next;
        });
      } else if (ev.type === 'message_deleted' && ev.message_id != null) {
        // Комната в событии не приходит — вычищаем из всех ключей этого чата (общая + комнаты).
        setMessages((prev) => {
          let changed = false;
          const next = new Map(prev);
          for (const [key, list] of next) {
            if (key === chatId || key.startsWith(chatId + ':')) {
              const filtered = list.filter((m) => m.id !== ev.message_id);
              if (filtered.length !== list.length) {
                next.set(key, filtered);
                changed = true;
              }
            }
          }
          return changed ? next : prev;
        });
      } else if (ev.type === 'read' && ev.user_id && ev.user_id !== userRef.current?.id && ev.message_id != null
        && userRef.current?.readReceiptsEnabled !== false) {
        const scope = ev.room_id === undefined ? '*' : roomKey(chatId, ev.room_id);
        const scopes = readCursorsRef.current.get(chatId) ?? new Map<string, Map<string, number>>();
        const cursors = scopes.get(scope) ?? new Map<string, number>();
        if (ev.message_id > (cursors.get(ev.user_id) ?? 0)) cursors.set(ev.user_id, ev.message_id);
        scopes.set(scope, cursors);
        readCursorsRef.current.set(chatId, scopes);
        const messageLists = () => [...messagesRef.current.entries()]
          .filter(([key]) => scope === '*' ? key === chatId || key.startsWith(chatId + ':') : key === scope)
          .map(([, list]) => list);
        const isGroup = chatsRef.current.find((c) => c.id === chatId)?.type === 2;
        if (!isGroup) {
          // Личный чат — получатель один, событие read сразу значит "прочитано всеми".
          setStatuses((prev) => {
            let next = prev;
            for (const list of messageLists()) for (const m of list) {
              if (m.senderId === userRef.current?.id && m.id <= ev.message_id!) next = upgradeStatus(next, m.id, 'read');
            }
            return next;
          });
        } else {
          // Группа — курсоры независимы по комнате; галочки синеют только после всех участников.
          const applyGroupReadStatuses = (memberIds: string[]) => {
            const myId = userRef.current?.id;
            const others = memberIds.filter((id) => id !== myId);
            if (others.length === 0) return;
            const minCursor = Math.min(...others.map((id) => cursors.get(id) ?? 0));
            if (minCursor <= 0) return;
            setStatuses((prev) => {
              let next = prev;
              for (const list of messageLists()) for (const m of list) {
                if (m.senderId === myId && m.id <= minCursor) next = upgradeStatus(next, m.id, 'read');
              }
              return next;
            });
          };
          const members = groupMembersRef.current.get(chatId);
          if (members) {
            applyGroupReadStatuses(members);
          } else if (!groupMembersLoadingRef.current.has(chatId)) {
            groupMembersLoadingRef.current.add(chatId);
            getChat(chatId)
              .then((chat) => {
                const ids = chat.members.map((m) => m.id);
                groupMembersRef.current.set(chatId, ids);
                applyGroupReadStatuses(ids);
              })
              .catch(() => {})
              .finally(() => groupMembersLoadingRef.current.delete(chatId));
          }
        }
      } else if (ev.type === 'delivered' && ev.user_id && ev.user_id !== userRef.current?.id && ev.message_id != null) {
        const scope = ev.room_id === undefined ? '*' : roomKey(chatId, ev.room_id);
        const lists = [...messagesRef.current.entries()]
          .filter(([key]) => scope === '*' ? key === chatId || key.startsWith(chatId + ':') : key === scope)
          .map(([, list]) => list);
        setStatuses((prev) => {
          let next = prev;
          for (const list of lists) for (const m of list) {
            if (m.senderId === userRef.current?.id && m.id <= ev.message_id!) next = upgradeStatus(next, m.id, 'delivered');
          }
          return next;
        });
      } else if (ev.type === 'read' && ev.user_id === userRef.current?.id) {
        // Подтверждение нашего маркера или чтение с другого устройства: сохраняем непрочитанный хвост.
        setChats((prev) => prev.map((c) => {
          if (c.id !== chatId) return c;
          if (ev.message_id == null) return ev.room_id === undefined ? { ...c, unreadCount: 0 } : clearRoomUnread(c, ev.room_id);
          const roomId = ev.room_id ?? null;
          const items = messagesRef.current.get(roomKey(chatId, roomId)) ?? [];
          const count = c.lastMessage && c.lastMessage.id <= ev.message_id
            ? 0 : remainingUnreadCount(items, unreadInRoom(c, roomId), ev.message_id, userRef.current?.id);
          if (!c.roomUnread) return { ...c, unreadCount: count };
          const roomUnread = c.roomUnread.map(entry => entry.roomId === roomId ? { ...entry, count } : entry);
          return { ...c, roomUnread, unreadCount: roomUnread.reduce((sum, entry) => sum + entry.count, 0) };
        }));
      } else if (ev.type === 'chat_deleted' || (ev.type === 'member_left' && ev.user_id === userRef.current?.id)) {
        // Группу удалили, либо меня из неё убрали/я вышел с другого устройства — убрать из списка.
        // Подписку снимаем: сервер больше не доставляет кадры этого чата, а при переподключении
        // подписка на недоступный чат была бы отвергнута (разрыв WS по кругу).
        realtime.unwatchChat(chatId);
        setChats((prev) => prev.filter((c) => c.id !== chatId));
        if (activeIdRef.current === chatId) {
          setActiveId(null);
          activeIdRef.current = null;
          activeRoomRef.current = null;
          localStorage.removeItem('hc_activeChat');
        }
      } else if (ev.type === 'title_changed' && ev.title) {
        // Группу переименовали — название пришло в самом событии, патчим список точечно.
        const title = ev.title;
        setChats((prev) => prev.map((c) => (c.id === chatId ? { ...c, title } : c)));
      } else if (ev.type === 'member_added') {
        // Добавили меня (или кого-то в мою группу) — состав/список чатов изменился.
        refreshChats();
      } else if (ev.type === 'avatar_changed') {
        // У собеседника/группы сменился аватар — своих данных для точечного патча нет,
        // проще перечитать список целиком (дёшево, редкое событие).
        refreshChats();
      }
    });
    return unsub;
  }, [refreshChats, setTyping]); // оба стабильны (useCallback с пустыми deps) — подписка не пересоздаётся

  // Редактирование / удаление сообщения (REST — сервер разошлёт события)
  const editMessage = useCallback(async (chatId: string, messageId: number, newText: string) => {
    // Берём исходный payload и меняем только text: вложения, ответ, пересылка и прочие поля
    // сохраняются (mediaIds в editMessagePayload считаются из payload.data.media).
    // entities зависят от позиций в тексте, а веб их не строит — при правке сбрасываем.
    let orig: MessageDto | undefined;
    for (const list of messagesRef.current.values()) {
      orig = list.find((m) => m.id === messageId);
      if (orig) break;
    }
    const { entities: _entities, ...data } = orig?.payload?.data ?? {};
    void _entities;
    await editMessagePayload(chatId, messageId, {
      ...(orig?.payload ?? { alg: 'plaintext' }),
      data: { ...data, text: newText },
    });
  }, []);
  const deleteMessage = useCallback(async (chatId: string, messageId: number) => {
    // Оптимистично скрываем сообщение; если сервер запретит операцию, восстановим его и
    // передадим ApiError в ChatView для toast. 404 означает, что оно уже удалено — оставляем скрытым.
    const removed = [...messagesRef.current.entries()].flatMap(([key, list]) =>
      list.filter((message) => message.id === messageId).map((message) => ({ key, message })),
    );
    setMessages((prev) => {
      const next = new Map(prev);
      for (const [cid, list] of next) {
        if (list.some((m) => m.id === messageId)) {
          next.set(cid, list.filter((m) => m.id !== messageId));
        }
      }
      return next;
    });
    try {
      await api(`/api/v1/chats/${chatId}/messages/${messageId}`, { method: 'DELETE' });
    } catch (error) {
      if (!(error instanceof ApiError && error.status === 404)) {
        setMessages((prev) => {
          const next = new Map(prev);
          for (const { key, message } of removed) {
            const list = next.get(key) ?? [];
            if (!list.some((item) => item.id === messageId)) {
              next.set(key, [...list, message].sort((a, b) => a.id - b.id));
            }
          }
          return next;
        });
      }
      throw error;
    }
  }, []);

  // Подгрузка более старых сообщений (при скролле вверх), в текущей комнате
  const loadOlder = useCallback(async (chatId: string, roomId?: string | null): Promise<OlderHistoryResult> => {
    const key = roomKey(chatId, roomId);
    if (startReachedRef.current.has(key)) return 'end';
    const list = messages.get(key);
    const cursor = historyBeforeRef.current.get(key);
    if (!canLoadOlderMessages(list, cursor)) return 'end';
    let before = cursor ?? list?.[0]?.id;
    if (before == null) return 'end';
    let nextBefore: number | null = before;
    let olderMessages: MessageDto[] = [];
    for (let pageIndex = 0; pageIndex < SUPPORT_DIAGNOSTIC_SCAN_MAX_PAGES; pageIndex++) {
      const page = await history(chatId, before, MESSAGE_PAGE_SIZE, roomId);
      const visible = visibleMessages(chatId, page.items);
      if (page.items.length === 0) {
        nextBefore = null;
        break;
      }
      olderMessages = [...visible, ...olderMessages];
      nextBefore = page.nextBefore;
      if (visible.length > 0 || nextBefore == null) break;
      before = nextBefore;
    }
    historyBeforeRef.current.set(key, nextBefore);
    if (nextBefore == null) startReachedRef.current.add(key);
    if (olderMessages.length === 0) return nextBefore == null ? 'end' : 'empty-more';
    setMessages((prev) => {
      const next = new Map(prev);
      const cur = next.get(key) ?? [];
      next.set(key, [...olderMessages.filter((item) => !cur.some((existing) => existing.id === item.id)), ...cur]);
      return next;
    });
    return 'visible';
  }, [messages, visibleMessages]);

  // «Назад» на мобильных: закрыть чат и вернуться к списку чатов целиком (используется из
  // колонки комнат — см. RoomsColumn onBack — и для чатов без комнат из самого ChatView).
  const closeChat = useCallback(() => {
    const id = activeIdRef.current;
    if (!id) return;
    setActiveId(null);
    activeIdRef.current = null;
    setActiveRoomId(null);
    activeRoomRef.current = null;
    setRoomsListOpen(false);
    roomsListOpenRef.current = false;
    localStorage.removeItem('hc_activeChat');
  }, []);

  // «Назад» из ChatView: у группы с комнатами ведёт не сразу к списку чатов, а на экран
  // "список комнат" (см. отчёт пользователя про мобильный сценарий "Чаты ← Комнаты ← Комната").
  // На десктопе это не проявляется (там нет отдельных "экранов" — оба списка видны разом), но
  // состояние всё равно нужно (см. .layout.rooms-mode.rooms-list-open в theme.css).
  const backFromChatView = useCallback(() => {
    const id = activeIdRef.current;
    const hasRooms = chatsRef.current.find((c) => c.id === id)?.hasRooms;
    if (hasRooms) {
      setRoomsListOpen(true);
      roomsListOpenRef.current = true;
    } else {
      closeChat();
    }
  }, [closeChat]);

  // Открытый чат может быть прокручен вверх: маркер двигаем только по видимым сообщениям.
  const visibleReadMarkersRef = useRef(new Map<string, number>());
  const markVisibleRead = useCallback((messageId: number) => {
    const chatId = activeIdRef.current;
    const roomId = activeRoomRef.current ?? null;
    const currentChat = chatsRef.current.find(chat => chat.id === chatId);
    if (!chatId || !currentChat || document.hidden
      || (roomsListOpenRef.current && mobileRoomPickerActive(currentChat.hasRooms === true))) return;
    const markerKey = userRef.current?.id + ':' + roomKey(chatId, roomId);
    if ((visibleReadMarkersRef.current.get(markerKey) ?? 0) >= messageId) return;
    visibleReadMarkersRef.current.set(markerKey, messageId);
    markRead(chatId, messageId, roomId).catch(() => {
      if (visibleReadMarkersRef.current.get(markerKey) === messageId) visibleReadMarkersRef.current.delete(markerKey);
    });
    setChats(prev => prev.map(chat => {
      if (chat.id !== chatId) return chat;
      const count = remainingUnreadCount(messagesRef.current.get(roomKey(chatId, roomId)) ?? [], unreadInRoom(chat, roomId), messageId, userRef.current?.id);
      if (!chat.roomUnread) return { ...chat, unreadCount: count };
      const roomUnread = chat.roomUnread.map(entry => entry.roomId === roomId ? { ...entry, count } : entry);
      return { ...chat, roomUnread, unreadCount: roomUnread.reduce((sum, entry) => sum + entry.count, 0) };
    }));
  }, []);

  // Смена комнаты открытого группового чата: ставим активную и грузим её историю, если ещё нет.
  const switchRoom = useCallback(async (roomId: string | null) => {
    const id = activeIdRef.current;
    if (!id) return;
    setActiveRoomId(roomId);
    activeRoomRef.current = roomId;
    const selectedChat = chatsRef.current.find((chat) => chat.id === id);
    chatUnreadOnOpenRef.current.set(id, selectedChat ? unreadInRoom(selectedChat, roomId) : 0);
    setRoomsListOpen(false); // выбор комнаты — переход с экрана "список комнат" на "сообщения"
    roomsListOpenRef.current = false;
    const key = roomKey(id, roomId);
    const cached = messages.get(key);
    if (cached) {
      return;
    }
    try {
      const page = await history(id, undefined, MESSAGE_PAGE_SIZE, roomId);
      const visible = visibleMessages(id, page.items);
      historyBeforeRef.current.set(key, page.nextBefore);
      if (page.nextBefore == null) startReachedRef.current.add(key); else startReachedRef.current.delete(key);
      setMessages((prev) => {
        if (prev.has(key)) return prev;
        const next = new Map(prev);
        next.set(key, visible);
        return next;
      });
    } catch {
      /* не критично — пусто */
    }
  }, [messages, visibleMessages]);

  const openChat = useCallback(
    async (id: string) => {
      setActiveId(id);
      activeIdRef.current = id;
      setActiveRoomId(null); // открываем на общей комнате
      activeRoomRef.current = null;
      // У группы с комнатами мобильный сценарий стартует с экрана "список комнат", а не сразу
      // с сообщений (см. отчёт пользователя); на десктопе флаг влияет только на CSS внутри
      // мобильного медиазапроса, здесь он ни на что не смотрит.
      const openingHasRooms = !!chatsRef.current.find((c) => c.id === id)?.hasRooms;
      setRoomsListOpen(openingHasRooms);
      roomsListOpenRef.current = openingHasRooms;
      localStorage.setItem('hc_activeChat', id);
      realtime.watchChat(id);
      // Синхронно, ДО фетча истории — ChatView получает unread только для General.
      const chatOnOpen = chatsRef.current.find((c) => c.id === id);
      const generalUnread = chatOnOpen ? unreadInRoom(chatOnOpen, null) : 0;
      chatUnreadOnOpenRef.current.set(id, generalUnread);
      const key = roomKey(id, null);
      let generalMessages = messages.get(key);
      if (!generalMessages) {
        try {
          const page = await history(id);
          generalMessages = visibleMessages(id, page.items);
          historyBeforeRef.current.set(key, page.nextBefore);
          if (page.nextBefore == null) startReachedRef.current.add(key); else startReachedRef.current.delete(key);
          setMessages((prev) => {
            const next = new Map(prev);
            next.set(key, generalMessages!);
            return next;
          });
        } catch {
          /* чат мог быть удалён */
        }
      }
    },
    [messages, visibleMessages],
  );
  openChatRef.current = openChat;

  const rememberSupportChat = (chat: ChatDto) => {
    if (!chat.isSupport) throw new Error(t('misc:feedbackChatUnavailable'));
    if (chatsRef.current.some((item) => item.id === chat.id)) return;
    // Новый чат уже доступен через API, даже если событие chat_added ещё не обработано.
    const summary: ChatSummary = {
      id: chat.id, type: chat.type, title: chat.title, avatarFileId: chat.avatarFileId,
      unreadCount: 0, membersPreview: chat.members.map((member) => member.id),
      isSupport: true, isNotes: false, archived: false, hasRooms: false,
    };
    chatsRef.current = [...chatsRef.current, summary];
    setChats(chatsRef.current);
  };

  const openChatByContact = useCallback(async (userId: string) => {
    const existing = chats.find((chat) => chat.type === 1 && chat.otherUserId === userId);
    if (existing) {
      await openChat(existing.id);
      return;
    }
    const chat = await createChat(1, null, [userId]);
    await refreshChats();
    await openChat(chat.id);
  }, [chats, openChat, refreshChats]);

  useEffect(() => {
    if (!authed || !pendingUserReference) return;
    let cancelled = false;
    setPendingUserBrief(null);
    setPendingUserError('');
    getUserBrief(pendingUserReference.userId)
      .then((brief) => { if (!cancelled) setPendingUserBrief(brief); })
      .catch((error: unknown) => {
        if (!cancelled) setPendingUserError(contactProfileError(error instanceof ApiError ? error.status : undefined));
      });
    return () => { cancelled = true; };
  }, [authed, pendingUserReference]);

  const clearPendingUserReference = useCallback(() => {
    setPendingUserReference(null);
    setPendingUserBrief(null);
    setPendingUserError('');
    try { sessionStorage.removeItem('hc_pendingUserReference'); } catch { /* приватный режим */ }
  }, []);

  // Переход к сообщению из результата глобального поиска (см. ChatList/GlobalSearchResults) —
  // открывает чат как обычно и просит ChatView проскроллить к сообщению, когда оно появится
  // в загруженной истории (best-effort, см. ChatView.jumpToMessageId).
  const [pendingJumpMessageId, setPendingJumpMessageId] = useState<number | null>(null);
  const openChatAtMessage = useCallback(
    (chatId: string, messageId: number) => {
      setPendingJumpMessageId(messageId);
      openChat(chatId);
    },
    [openChat],
  );

  // Web Push: service worker, перепривязка уже существующей подписки к этой сессии и переход
  // в чат по клику на push-уведомление (см. push.ts, public/sw.js).
  useEffect(() => {
    if (!authed) return;
    registerServiceWorker().then(() => syncPushSubscription());
    return listenPushNavigation((chatId) => openChatRef.current(chatId), (chatId) => {
      void markNotificationChatRead(chatId).then(() => refreshChats()).catch(() => openChatRef.current(chatId));
    });
  }, [authed, refreshChats]);

  useEffect(() => {
    if (!authed) return;
    requestNotificationPermission();
    // Support summaries contain a protected flag and unread count; fetch identity before the list
    // so diagnostics cannot briefly leak while role is still unknown on cold start.
    let cancelled = false;
    let retryTimer: number | null = null;
    const refreshController = new AbortController();
    const initialReady = (async () => {
      let delay = 1000;
      while (!cancelled) {
        try {
          await refreshChats(refreshController.signal);
          if (cancelled) return;
          if (!userRef.current) throw new Error('Identity is not loaded');
          realtime.connect();
          return;
        } catch {
          if (cancelled) return;
          await new Promise<void>((resolve) => {
            retryTimer = window.setTimeout(resolve, delay);
          });
          delay = Math.min(delay * 2, 30000);
        }
      }
    })();

    // Реконнект WS после разрыва (обрыв сети/рестарт бэкенда) — молча подтягиваем список
    // чатов заново, без ручного F5: сообщения в открытых чатах уже досинхронизированы
    // через polling-фоллбэк, а вот новые/удалённые чаты, смена аватара и т.п. — нет.
    // Самый первый connect пропускаем — refreshChats() для него уже вызван выше.
    let firstConnect = true;
    const offStatus = realtime.onStatus((connected) => {
      if (!connected) return;
      if (!firstConnect) refreshChats();
      firstConnect = false;
    });

    // Лог /sync обрезан дальше нашего курсора — пропущенное не вернуть, делаем полную сверку:
    // список чатов и лента открытой комнаты перечитываются целиком.
    const offResync = realtime.onResync(async (signal) => {
      await refreshChats(signal);
      if (signal.aborted) return;
      const id = activeIdRef.current;
      if (!id) return;
      const room = activeRoomRef.current;
      const page = await history(id, undefined, MESSAGE_PAGE_SIZE, room);
      if (signal.aborted) return;
      const key = roomKey(id, room);
      const visible = visibleMessages(id, page.items);
      historyBeforeRef.current.set(key, page.nextBefore);
      if (page.nextBefore == null) startReachedRef.current.add(key); else startReachedRef.current.delete(key);
      setMessages((prev) => {
        const next = new Map(prev);
        next.set(key, visible);
        return next;
      });
    });

    // Восстановление открытого чата после перезагрузки страницы (общая комната)
    void initialReady.then(() => {
      if (cancelled) return;
      const restoreChatId = activeIdRef.current;
      const restoreRoomId = activeRoomRef.current;
      if (!restoreChatId || !chatsRef.current.some((chat) => chat.id === restoreChatId)) return;
      const key = roomKey(restoreChatId, restoreRoomId);
      if (!messages.has(key)) {
        // Сначала identity и список подтверждают роль и доступ; только затем восстанавливаем ленту.
        history(restoreChatId, undefined, MESSAGE_PAGE_SIZE, restoreRoomId)
          .then((page) => {
            if (cancelled) return;
            historyBeforeRef.current.set(key, page.nextBefore);
            if (page.nextBefore == null) startReachedRef.current.add(key); else startReachedRef.current.delete(key);
            const visible = visibleMessages(restoreChatId, page.items);
            setMessages((prev) => {
              const next = new Map(prev);
              next.set(key, visible);
              return next;
            });
          })
          .catch(() => {});
      }
    });

    const offMsg = realtime.onMessage((msg) => {
      // WS-топик и polling-фоллбэк могут пересечься при реконнекте (особенно при
      // рестарте бэкенда: WS рвётся -> фоллбэк на /sync -> STOMP сам восстанавливается)
      // и прислать одно и то же сообщение дважды. Список сообщений ниже дедуплицирует
      // по id, но side-эффекты (счётчик непрочитанных, звук) идут до его коммита —
      // без проверки здесь они бы задваивались при каждом повторном приходе.
      const msgKey = roomKey(msg.chatId, msg.roomId);
      if ((messagesRef.current.get(msgKey) ?? []).some((m) => m.id === msg.id)) return;
      if (msg.senderId !== userRef.current?.id) setTyping(msg.chatId, msg.senderId, false);
      // Чата нет в списке (например, добавили в группу) — подтянуть список целиком.
      if (chatsRef.current.length > 0 && !chatsRef.current.some((c) => c.id === msg.chatId)) refreshChats();
      const activeChat = chatsRef.current.find((chat) => chat.id === msg.chatId);
      const supportOperator = userRef.current?.role === 'admin' || userRef.current?.role === 'support';
      const roomPickerVisible = roomsListOpenRef.current && mobileRoomPickerActive(activeChat?.hasRooms === true);
      const isActive = msg.chatId === activeIdRef.current
        && (msg.roomId ?? null) === (activeRoomRef.current ?? null)
        && !roomPickerVisible;
      const technicalDiagnostic = isTechnicalDiagnosticMessage(msg, activeChat?.isSupport === true);
      if (technicalDiagnostic && isActive) markRead(msg.chatId, msg.id, msg.roomId ?? null).catch(() => {});
      if (technicalDiagnostic && !supportOperator) return;
      setMessages((prev) => {
        const list = prev.get(msgKey) ?? [];
        if (list.some((m) => m.id === msg.id)) return prev;
        const next = new Map(prev);
        next.set(msgKey, [...list, msg]);
        return next;
      });
      // Своё же сообщение (например, отправленное форвардом в чат, который сейчас не открыт)
      // не должно становиться "непрочитанным" самому себе — иначе бейдж появляется без
      // единого реального непрочитанного сообщения от собеседника.
      const isMine = msg.senderId === userRef.current?.id;
      if (!isMine) {
        // Сообщение дошло до устройства, но не просматривается сейчас — только delivered.
        markDelivered(msg.chatId, msg.id, msg.roomId ?? null).catch(() => {});
        const chatForNotif = chatsRef.current.find((c) => c.id === msg.chatId);
        if (!isActive && supportNotificationAllowed(msg, chatForNotif, supportOperator, cachedSupportNotificationPrefs()) && !isChatMutedAt(chatForNotif)) {
          playMessageSound();
          const body =
            chatForNotif?.type === 2 && msg.senderName ? `${msg.senderName}: ${notificationBody(msg)}` : notificationBody(msg);
          showMessageNotification(chatForNotif?.title || 'HomeChat', body, () => openChatRef.current(msg.chatId), msg.chatId);
        }
      }
      setChats((prev) =>
        prev.map((c) => {
          if (c.id !== msg.chatId) return c;
          const next = { ...c, lastMessage: msg, lastMessageAt: msg.createdAt };
          if (c.roomUnread) {
            const roomId = msg.roomId ?? null;
            const found = c.roomUnread.some((entry) => entry.roomId === roomId);
            const roomUnread = found
              ? c.roomUnread.map((entry) => entry.roomId === roomId
                ? { ...entry, count: isMine ? entry.count : entry.count + 1 }
                : entry)
              : [...c.roomUnread, { roomId, count: isMine ? 0 : 1 }];
            return { ...next, roomUnread, unreadCount: roomUnread.reduce((sum, entry) => sum + entry.count, 0) };
          }
          return { ...next, unreadCount: isMine ? c.unreadCount : c.unreadCount + 1 };
        }),
      );
    });
    const offAck = realtime.onAck((ack) => {
      if (ack.status === 'ok' && ack.message) {
        setStatuses((prev) => upgradeStatus(prev, ack.message.id, 'sent'));
      }
    });
    const offUser = realtime.onUser((ev) => {
      const e = ev as { type?: string; role?: string; displayName?: string };
      // Меня добавили в чат (на его topic я ещё не подписан) — подтянуть список.
      if (e.type === 'member_added') refreshChats();
      if (e.type === 'role_changed' && e.role) {
        // Админ сменил роль — применяем сразу, без ручной перезагрузки страницы.
        // Если админку у пользователя только что отобрали, а она была открыта — закрыть.
        setUser((prev) => (prev ? { ...prev, role: e.role } : prev));
        if (e.role !== 'admin' && e.role !== 'support') setAdminOpen(false);
        // Бэкенд при смене роли заодно добавляет в чат "Поддержка" (повышение до admin/support,
        // см. AdminService.setRole, отчёт "Аптук не видит чат поддержки") либо убирает из него
        // (понижение до user, отчёт "po007111 указан как владелец чата поддержки при этом у
        // него роль пользователя") — в обоих направлениях без refreshChats() список чатов
        // отразил бы это только после ручной перезагрузки страницы.
        refreshChats();
      } else if (e.type === 'display_name_changed' && e.displayName) {
        const displayName = e.displayName;
        setUser((prev) => (prev ? { ...prev, displayName } : prev));
      }
    });

    return () => {
      cancelled = true;
      refreshController.abort();
      if (retryTimer != null) window.clearTimeout(retryTimer);
      offMsg();
      offAck();
      offUser();
      offStatus();
      offResync();
      realtime.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const outboxOwnerId = authed ? user?.id : undefined;
  useEffect(() => {
    if (!outboxOwnerId) return;
    realtime.startOutbox(outboxOwnerId);
    // kick также проверяет смену входа в соседней вкладке и отменяет старый запрос.
    const onStorage = outbox.kick;
    window.addEventListener('online', outbox.kick);
    window.addEventListener('storage', onStorage);
    const offStatus = realtime.onStatus(connected => { if (connected) outbox.kick(); });
    return () => {
      window.removeEventListener('online', outbox.kick);
      window.removeEventListener('storage', onStorage);
      offStatus();
      outbox.stop();
    };
  }, [outboxOwnerId]);

  const handleLogout = async () => {
    outbox.stop();
    // До logout(): отписка требует ещё живого access-токена.
    // Активный звонок v2 завершаем корректно (end/decline) и освобождаем медиа до разлогина.
    const v2 = sessionRef.current;
    if (v2 instanceof RtcV2Session) {
      v2.hangup();
      cleanupCall();
    }
    await disablePushOnLogout();
    await logout();
    localStorage.removeItem('hc_activeChat');
    if (user) clearUserDrafts(user.id);
    setAuthed(false);
    setUser(null);
    setChats([]);
    setActiveId(null);
    activeIdRef.current = null;
    setActiveRoomId(null);
    activeRoomRef.current = null;
    setRoomsListOpen(false);
    roomsListOpenRef.current = false;
    setMessages(new Map());
  };

  if (!authed) {
    return <Login onAuthed={() => setAuthed(true)} />;
  }

  const selectedChat = chats.find((c) => c.id === activeId) ?? null;
  const activeChat = (user?.role === 'admin' || user?.role === 'support') && selectedChat?.isSupport && supportApplicant?.chatId === selectedChat.id && supportApplicant.session === getAuthSession()
    ? { ...selectedChat, title: t('chats:supportChat') + ': ' + supportApplicant.name }
    : selectedChat;
  // Группа с включёнными комнатами — список чатов сворачивается до аватарок, освободившееся
  // место занимает колонка комнат (см. ChatList collapsed / RoomsColumn, отчёт пользователя
  // "комнаты в групповых чатах надо переделать... в основном чате сворачивать до аватарки
  // чаты - в свободном месте отобразить комнаты").
  const roomsMode = !!activeChat?.hasRooms && !settingsOpen;

  const closeJoin = () => {
    try { sessionStorage.removeItem('hc_joinToken'); } catch { /* ignore */ }
    setJoinToken(null);
  };

  return (
    <div
      className={
        'layout'
        + (activeId && !settingsOpen ? ' chat-open' : '')
        + (roomsMode ? ' rooms-mode' : '')
        + (roomsMode && roomsListOpen ? ' rooms-list-open' : '')
      }
      style={{ '--sidebar-width': `${sidebarWidth}px` } as CSSProperties}
    >
      <AltEndpointBanner endpoints={alternateEndpoints} />
      <InstallAppBanner />
      <UpdateAvailableBanner onOpenAbout={(platform) => {
        setUpdateAboutRequest(value => value + 1);
        setUpdateAboutPlatform(platform);
        setSettingsInitialTab('apps');
        setSettingsOpen(true);
      }} />
      <AccessNeededBanner inCall={call !== null} failure={mediaFailure} onClearFailure={() => setMediaFailure(null)} />
      {settingsOpen ? (
        <ErrorBoundary>
          <Suspense fallback={null}>
            <SettingsModal
              me={user}
              chats={chats}
              key={String(settingsInitialTab) + updateAboutRequest}
              initialTab={settingsInitialTab}
              updatePlatform={updateAboutPlatform}
              onWriteFeedback={async (version) => {
                if (!user) throw new Error(t('misc:feedbackChatUnavailable'));
                const chatId = await prepareReleaseFeedback(user.id, async () => {
                  const conversation = await getSupportConversation();
                  await refreshChats();
                  if (userRef.current?.id !== user.id) throw new Error(t('misc:feedbackChatUnavailable'));
                  rememberSupportChat(conversation);
                  return conversation;
                }, t('misc:feedbackDraft', { version }));
                if (userRef.current?.id !== user.id) return;
                if (!chatId) throw new Error(t('misc:feedbackChatUnavailable'));
                // Перечитываем черновик и при уже открытой «Поддержке».
                setFeedbackComposerVersion((value) => value + 1);
                void openChat(chatId);
                setSettingsOpen(false);
              }}
              onClose={() => setSettingsOpen(false)}
              onUserUpdate={setUser}
              onChatsChanged={refreshChats}
              onFoldersChanged={() => setFoldersVersion((v) => v + 1)}
            />
          </Suspense>
        </ErrorBoundary>
      ) : (
        <ChatList
          chats={chats}
          activeId={activeId}
          user={user}
          onOpen={openChat}
          onOpenSupport={async (chat, applicant) => {
            rememberSupportChat(chat);
            setSupportApplicant(applicant ? { session: getAuthSession(), chatId: chat.id, name: applicant.displayName || applicant.username } : null);
            await openChat(chat.id);
          }}
          onRefresh={refreshChats}
          onLogout={handleLogout}
          onOpenSettings={(tab) => { setSettingsInitialTab(tab ?? null); setSettingsOpen(true); }}
          onOpenAdmin={() => setAdminOpen(true)}
          foldersVersion={foldersVersion}
          contactsVersion={contactsVersion}
          onOpenMessage={openChatAtMessage}
          collapsed={roomsMode}
        />
      )}
      {!roomsMode && !settingsOpen && (
        <SidebarResizer width={sidebarWidth} onWidthChange={setSidebarWidth} />
      )}
      {joinToken && (
        <JoinInviteModal
          token={joinToken}
          onClose={closeJoin}
          onJoined={(chatId) => { closeJoin(); refreshChats(); openChat(chatId); }}
        />
      )}
      {pendingContactQr && user && (
        <ContactLinkPreviewModal
          clusterId={pendingContactQr.clusterId}
          token={pendingContactQr.token}
          onClose={() => { clearPendingContactQr(); setPendingContactQr(null); }}
          onAdded={() => setContactsVersion((v) => v + 1)}
          onOpenChat={openChatByContact}
        />
      )}
      {pendingUserReference && user && (
        <div
          className="modal-backdrop"
          onClick={clearPendingUserReference}
        >
          <div className="modal" onClick={(event) => event.stopPropagation()} style={{ maxWidth: 380 }}>
            <button
              type="button"
              className="modal-close"
              title={t('close')}
              onClick={clearPendingUserReference}
            >✕</button>
            <h2>{t('openContact')}</h2>
            {pendingUserError && <div className="error" style={{ marginBottom: 12 }}>{pendingUserError}</div>}
            {pendingUserBrief ? (
              <>
                <div style={{ fontSize: 16, margin: '8px 0 16px' }}>
                  {pendingUserBrief.displayName || `@${pendingUserBrief.username}`}
                  {pendingUserBrief.username && <span style={{ color: 'var(--muted)' }}> · @{pendingUserBrief.username}</span>}
                </div>
                {contactUsernameMismatch(pendingUserReference.expectedUsername, pendingUserBrief.username) && (
                  <div className="error" style={{ marginBottom: 12 }}>{t('contactUsernameMismatch')}</div>
                )}
                <div className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      const id = pendingUserReference.userId;
                      void openChatByContact(id).then(clearPendingUserReference)
                        .catch(() => setPendingUserError(t('openWebChatFailed')));
                    }}
                  >{t('openInWeb')}</button>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      window.location.href = desktopContactUri(
                        pendingUserReference.userId,
                        pendingUserReference.expectedUsername,
                      );
                    }}
                  >{t('openInDesktop')}</button>
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 10 }}>
                  {t('desktopLinkHint')}
                </div>
              </>
            ) : !pendingUserError ? <span className="spinner" /> : null}
          </div>
        </div>
      )}
      {roomsMode && activeChat && (
        <RoomsColumn chat={activeChat} me={user} roomId={activeRoomId} onRoomChange={switchRoom} onBack={closeChat} />
      )}
      <div className="main">
        {activeId ? (
          <ChatView
            key={`${user?.id}:${activeId}:${activeRoomId ?? ''}:${feedbackComposerVersion}`}
            chatId={activeId}
            chat={activeChat}
            chats={chats}
            messages={messages.get(roomKey(activeId, activeRoomId)) ?? []}
            historyLoaded={messages.has(roomKey(activeId, activeRoomId))}
            me={user}
            onLoadOlder={loadOlder}
            onBack={backFromChatView}
            statuses={statuses}
            onEditMessage={editMessage}
            onDeleteMessage={deleteMessage}
            onStartCall={startCall}
            callActive={!!call || !!groupCall}
            onChatsChanged={refreshChats}
            typingUserIds={[...(typingByChat.get(activeId) ?? [])]}
            onOpenChat={openChat}
            roomId={activeRoomId}
            jumpToMessageId={pendingJumpMessageId}
            onJumpHandled={() => setPendingJumpMessageId(null)}
            unreadOnOpen={chatUnreadOnOpenRef.current.get(activeId) ?? 0}
            unreadCount={activeChat ? unreadInRoom(activeChat, activeRoomId) : 0}
            onVisibleRead={markVisibleRead}
          />
        ) : (
          <div className="center-screen">
            <p style={{ color: 'var(--muted)' }}>{t('selectChat')}</p>
          </div>
        )}
      </div>

      {adminOpen && (user?.role === 'admin' || user?.role === 'support') && (
        <Suspense fallback={null}>
          <AdminPanel onClose={() => setAdminOpen(false)} isSupport={user?.role === 'support'} />
        </Suspense>
      )}

      {call && (
        <Suspense fallback={null}>
          <CallOverlay
            mode={call.mode}
            remoteStream={remoteStream}
            localStream={localStream}
            video={callVideoRef.current}
            mediaWarning={callMediaWarning}
            micMuted={localMuted.audio}
            cameraMuted={localMuted.video}
            onAccept={needsCallPreparation(call.mode, callVideoRef.current) ? () => { void prepareVideoAnswer(); } : acceptCall}
            onReject={rejectCall}
            preparationDevices={preparationDevices}
            selectedPreparationCameraId={selectedPreparationCameraId}
            preparationError={preparationError}
            onSelectPreparationCamera={changePreparationCamera}
            onTogglePreparationMic={togglePreparationMic}
            onTogglePreparationCamera={togglePreparationCamera}
            onHangup={hangup}
            onSwitchCamera={() => sessionRef.current?.switchCamera()}
            onToggleMic={toggleMic}
            onToggleCamera={toggleCamera}
            localHeld={localHeld}
            remoteHeld={remoteHeld}
            onToggleHold={toggleHold}
            remoteMuted={remoteMuted}
          />
        </Suspense>
      )}

      {groupCall && (
        <Suspense fallback={null}>
          <GroupCallOverlay
            mode={groupCall.mode}
            isCreator={groupCall.isCreator}
            onJoin={joinGroupCallV2}
            onDecline={declineGroupCallV2}
            participantIds={[...new Set([...groupRoster, ...groupStreams.keys()])]}
            streams={groupStreams}
            localStream={groupLocalStream}
            onLeave={leaveGroupCall}
            micMuted={groupLocalMuted.audio}
            cameraMuted={groupLocalMuted.video}
            onToggleMic={toggleGroupMic}
            onToggleCamera={toggleGroupCamera}
            peerMuted={groupPeerMuted}
            mediaWarning={callMediaWarning}
          />
        </Suspense>
      )}
      {/* Не удалось получить ни микрофон, ни камеру ДО того, как звонок вообще начался (call/
          groupCall ещё не выставлены — оверлея нет, показывать предупреждение внутри него
          некуда) — см. showCallFailedWarning. Пока звонок уже идёт, то же сообщение показывает
          сам оверлей (CallOverlay/GroupCallOverlay, mediaWarning выше), здесь не дублируем. */}
      {callMediaWarning && !call && !groupCall && (
        <div className="call-media-warning-standalone">{callMediaWarning}</div>
      )}
      <TransferPolicyPrompt />
    </div>
  );
}
